import React from 'react';
import { Search } from 'lucide-react';
import Pagination from '@mui/material/Pagination';
import { getPropImagePath, handleImageError } from '../../../utils/imageUtils';

const WarehousesView = ({
  paginatedProps,
  totalPages,
  currentPage,
  onPageChange,
  organizations,
  addToCart,
  setSelectedProp,
  setSelectedFilters,
  selectedFilters,
  setCurrentView
}) => {
  // Browse by warehouse/organization first
  const propsByOrg = paginatedProps.reduce((acc, prop) => {
    if (!acc[prop.owner]) {
      acc[prop.owner] = [];
    }
    acc[prop.owner].push(prop);
    return acc;
  }, {});
  
  const sortedOrgs = Object.entries(propsByOrg).sort((a, b) => {
    const aDistance = Math.min(...a[1].map(p => p.distance));
    const bDistance = Math.min(...b[1].map(p => p.distance));
    return aDistance - bDistance;
  });

  return (
    <div className="space-y-6">
      {sortedOrgs.length > 0 ? (
        <>
          {sortedOrgs.map(([orgName, orgProps]) => {
            const minDistance = Math.min(...orgProps.map(p => p.distance));
            const availableCount = orgProps.filter(p => p.status === 'available').length;
            const org = organizations.find(o => o.name === orgName);
            
            return (
              <div key={orgName} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {/* Organization Header */}
                <div className="bg-gray-50 border-b p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold text-lg">{orgName}</h3>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                        <span className="flex items-center gap-1">
                          📍 {Math.round(minDistance)} miles away
                        </span>
                        <span className="flex items-center gap-1">
                          📦 {orgProps.length} props
                        </span>
                        <span className="flex items-center gap-1">
                          ✅ {availableCount} available
                        </span>
                        {org?.location && (
                          <span className="text-gray-500">{org.location}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">{orgProps.length}</div>
                      <div className="text-xs text-gray-500">props total</div>
                    </div>
                  </div>
                </div>
                
                {/* Props Grid for this Organization */}
                <div className="p-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {orgProps.slice(0, 12).map(prop => (
                      <div key={prop.id} className="group relative">
                        <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                          <img 
                            src={getPropImagePath(prop)}
                            alt={prop.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            onError={(e) => handleImageError(e, prop.category)}
                          />
                          
                          {/* Status indicator */}
                          <div className="absolute top-1 left-1">
                            <div className={`w-3 h-3 rounded-full ${
                              prop.status === 'available' ? 'bg-green-500' :
                              prop.status === 'checked_out' ? 'bg-orange-500' :
                              prop.status === 'reserved' ? 'bg-purple-500' :
                              'bg-red-500'
                            }`}></div>
                          </div>
                          
                          {/* Quick actions on hover */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <div className="flex gap-1">
                              {prop.status === 'available' && (
                                <button
                                  onClick={() => addToCart(prop.id)}
                                  className="bg-blue-600 text-white p-1 rounded text-xs hover:bg-blue-700"
                                  title="Add to Cart"
                                >
                                  +
                                </button>
                              )}
                              <button
                                onClick={() => setSelectedProp(prop)}
                                className="bg-white text-gray-900 p-1 rounded text-xs hover:bg-gray-100"
                                title="View Details"
                              >
                                👁
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Prop name */}
                        <div className="mt-1 text-xs font-medium truncate" title={prop.name}>
                          {prop.name}
                        </div>
                        <div className="text-xs text-gray-500">{prop.category}</div>
                      </div>
                    ))}
                    
                    {/* Show more indicator */}
                    {orgProps.length > 12 && (
                      <div className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-500">
                        <div className="text-center">
                          <div className="text-lg font-bold">+{orgProps.length - 12}</div>
                          <div className="text-xs">more</div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* View all props from this org */}
                  {orgProps.length > 6 && (
                    <div className="mt-4 text-center">
                      <button 
                        onClick={() => {
                          // Filter to just this organization
                          setSelectedFilters({...selectedFilters, organization: orgName});
                          setCurrentView('grid');
                        }}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        View all {orgProps.length} props from {orgName} →
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <Pagination 
            totalPages={totalPages} 
            currentPage={currentPage} 
            onPageChange={onPageChange} 
          />
        </>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Search className="h-16 w-16 mx-auto mb-4 opacity-50" />
          <p>No warehouses found with matching props</p>
        </div>
      )}
    </div>
  );
};

export default WarehousesView;